import dayjs from 'dayjs';
import API from './API';
import { fetchPaged, normalize, MAX_ROWS } from './crud';

// -----------------------------------------------------------------------------
//  CLIENTI E PACCHETTI VALET — le letture della pagina Clienti.
//
//  Stesso impianto della pagina Clienti del gestionale admin
//  (`customerAPI.getCustomersPaged` e compagni): pagine chieste al server,
//  ricerca applicata dal database, conteggi separati per i riquadri.
//
//  ⚠️ `s` E `filter` NON SI SOMMANO. Con @nestjsx/crud, se nella richiesta c'e'
//  il parametro di ricerca `s`, i `filter` vengono IGNORATI. Per questo ogni
//  condizione — compreso il partner — sta dentro l'unico JSON di `s`.
//
//  ⚠️ PACCHETTO VALET = entita' `Minute`, legata all'AUTO e non al cliente
//  (vedi le trappole di naming). Il cliente si raggiunge da `car.customer`.
// -----------------------------------------------------------------------------

export const PAGE_SIZE = 25;

// Righe da non mostrare mai: utenze di servizio e cancellati logici. Stesso
// criterio dell'admin.
const ESCLUSI = [
  { role: { $ne: 'admin' } },
  { mail: { $ne: 'Eliminato' } },
];

function conPagine(res) {
  const { rows, total } = normalize(res);
  return { rows, total, pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)) };
}

/**
 * Condizioni dell'anagrafica.
 *
 * ⚠️ Il filtro per partner lo mette il FRONTEND: `/customer` non ha
 * `@TenantCrudAuth` lato backend (a differenza di `/minute`). E' un dato da
 * sistemare li', non qui.
 *
 * La ricerca comprende la TARGA: per questo la lettura porta anche
 * il join `cars`, che il backend ammette da allora e che serve pure alla
 * colonna «Auto» dell'elenco.
 */
function condizioniClienti(partnerId, cerca, tipo = null) {
  const and = [
    { 'partners.id': { $eq: partnerId } },
    { is_deleted: { $eq: false } },
    { is_alive: { $eq: true } },
    ...ESCLUSI,
  ];
  // Azienda = ha una ragione sociale. È così che la distingue anche il form di
  // creazione (privato / azienda): non esiste una colonna «tipo».
  if (tipo === 'aziende') {
    and.push({ company_name: { $notnull: true } });
    and.push({ company_name: { $ne: '' } });
  }
  if (tipo === 'clienti') {
    and.push({ $or: [{ company_name: { $isnull: true } }, { company_name: { $eq: '' } }] });
  }
  const t = (cerca || '').trim();
  if (t) {
    and.push({
      $or: [
        { name: { $contL: t } },
        { surname: { $contL: t } },
        { company_name: { $contL: t } },
        { mail: { $contL: t } },
        { telephone_number: { $contL: t } },
        { city: { $contL: t } },
        { 'cars.targa': { $contL: t } },
      ],
    });
  }
  return JSON.stringify({ $and: and });
}

/**
 * Condizioni dei pacchetti.
 *
 * «Da confermare» = `accepted` false; «confermati» = `accepted` true. Il
 * partner non conferma: il pacchetto proposto lo accetta Simba.
 * I `NULL` storici restano fuori da entrambe, come nella pagina di prima.
 *
 * `confermati = null` toglie il filtro: si vedono TUTTI i pacchetti del
 * partner, confermati o no — compresi i `NULL` storici.
 */
function condizioniPacchetti(partnerId, confermati, cerca) {
  const and = [{ partnerId: { $eq: partnerId } }];
  if (confermati !== null && confermati !== undefined) {
    and.push({ accepted: { $eq: confermati } });
  }
  const t = (cerca || '').trim();
  if (t) {
    and.push({
      $or: [
        { 'car.targa': { $contL: t } },
        { 'car.marca': { $contL: t } },
        { 'car.modello': { $contL: t } },
        { 'car.customer.name': { $contL: t } },
        { 'car.customer.surname': { $contL: t } },
        { 'car.customer.mail': { $contL: t } },
      ],
    });
  }
  return JSON.stringify({ $and: and });
}

/** Una pagina dell'anagrafica, ordinata per cognome. */
export async function clientiPagina({ partnerId, page = 1, cerca = '', tipo = null }) {
  const res = await API.get('/customer', {
    params: {
      page,
      limit: PAGE_SIZE,
      // `cars` sempre: serve al filtro sulla targa e alla
      // colonna «Auto» dell'elenco.
      join: ['partners', 'cars'],
      sort: tipo === 'aziende' ? 'company_name,ASC' : 'surname,ASC',
      s: condizioniClienti(partnerId, cerca, tipo),
    },
  });
  return conPagine(res);
}

/** Quanti clienti ha il partner (il riquadro «Clienti»). */
export async function contaClienti(partnerId, tipo = null) {
  const res = await API.get('/customer', {
    params: { page: 1, limit: 1, join: ['partners'], s: condizioniClienti(partnerId, '', tipo) },
  });
  return normalize(res).total;
}

/** Una pagina di pacchetti, da confermare o confermati, i piu' recenti prima. */
export async function pacchettiPagina({ partnerId, confermati, page = 1, cerca = '' }) {
  const res = await API.get('/minute', {
    params: {
      page,
      limit: PAGE_SIZE,
      join: ['car', 'car.customer'],
      sort: 'data_reg,DESC',
      s: condizioniPacchetti(partnerId, confermati, cerca),
    },
  });
  return conPagine(res);
}

/**
 * Quando è stato usato un pacchetto valet: le date delle prenotazioni che lo
 * hanno consumato, dalla più recente.
 *
 * Il legame sta sul booking (`valet_minute_id`, scritto dal server quando
 * scala un valet): non c'è un registro dei consumi da nessun'altra parte.
 */
export async function consumiValet(minuteIds) {
  const ids = (minuteIds || []).filter(Boolean);
  if (!ids.length) return {};
  const res = await API.get('/booking', {
    params: {
      limit: 200,
      sort: 'data,DESC',
      fields: 'id,data,valet_minute_id,state',
      s: JSON.stringify({
        $and: [{ valet_minute_id: { $in: ids } }, { state: { $ne: 'canceled' } }],
      }),
    },
  });
  const per = {};
  normalize(res).rows.forEach((b) => {
    if (!b.valet_minute_id) return;
    (per[b.valet_minute_id] = per[b.valet_minute_id] || []).push(b.data);
  });
  return per;
}

/** Quanti pacchetti da confermare (o confermati). */
export async function contaPacchetti(partnerId, confermati) {
  const res = await API.get('/minute', {
    params: { page: 1, limit: 1, s: condizioniPacchetti(partnerId, confermati, '') },
  });
  return normalize(res).total;
}

/**
 * I pacchetti dei clienti di UNA pagina, per la colonna «Pacchetto valet».
 *
 * Una sola richiesta per pagina, con gli id dei clienti visibili: niente
 * download di tutti i pacchetti del partner per mostrarne 25.
 */
export async function pacchettiDeiClienti(partnerId, customerIds) {
  if (!customerIds.length) return [];
  const res = await API.get('/minute', {
    params: {
      page: 1,
      limit: MAX_ROWS,
      join: ['car', 'car.customer'],
      s: JSON.stringify({
        $and: [
          { partnerId: { $eq: partnerId } },
          { 'car.customer.id': { $in: customerIds } },
        ],
      }),
    },
  });
  return normalize(res).rows;
}

/** Tutta l'anagrafica, per l'export Excel. `truncated` va detto all'utente. */
export function tuttiIClienti(partnerId) {
  return fetchPaged('/customer', {
    join: ['partners'],
    sort: 'surname,ASC',
    s: condizioniClienti(partnerId, ''),
  });
}

/**
 * Lo stato di un pacchetto, con la STESSA regola che applicano le
 * prenotazioni (`pickup.service.ts`, `escort.service.ts`): un valet si scala
 * solo se il pacchetto e' confermato e la scadenza non e' PRIMA di oggi, a
 * livello di giorno. Un pacchetto che scade oggi si usa ancora.
 *
 * ⚠️ Senza data di scadenza il backend lo tratta come SCADUTO
 * (`dayjs(new Date(null))` è il 1° gennaio 1970): qui lo si dice invece di
 * mostrarlo «Attivo».
 */
export function statoPacchetto(m, oggi = dayjs()) {
  if (m?.accepted !== true) {
    return { kind: 'attn', label: 'In attesa di Simba', utilizzabile: false };
  }
  if (!m.expiration_valet) {
    return { kind: 'danger', label: 'Scadenza mancante', utilizzabile: false };
  }
  if (dayjs(m.expiration_valet).isBefore(oggi, 'day')) {
    return { kind: 'danger', label: 'Scaduto', utilizzabile: false };
  }
  if (!(Number(m.residual_valet) > 0)) {
    return { kind: 'danger', label: 'Esaurito', utilizzabile: false };
  }
  return { kind: 'ok', label: 'Attivo', utilizzabile: true };
}

/**
 * Il riassunto dei pacchetti di un cliente, per la sua riga in anagrafica.
 * Un cliente con due auto puo' avere due pacchetti: conta prima quello che
 * chiede un'azione (in attesa), poi quelli usabili (con i residui sommati).
 */
export function riepilogoValetCliente(pacchetti) {
  if (!pacchetti?.length) return null;
  const stati = pacchetti.map((m) => ({ m, s: statoPacchetto(m) }));
  const usabili = stati.filter((x) => x.s.utilizzabile);
  const residui = usabili.reduce((t, x) => t + (Number(x.m.residual_valet) || 0), 0);

  if (stati.some((x) => x.m.accepted !== true)) {
    return { kind: 'attn', label: 'In attesa di Simba', residui: usabili.length ? residui : null };
  }
  if (usabili.length) return { kind: 'ok', label: 'Attivo', residui };
  return { kind: 'danger', label: stati[0].s.label, residui: null };
}

// -----------------------------------------------------------------------------
//  PREZZO AL CLIENTE di un pacchetto già salvato.
//
//  Il prezzo del venditore è salvato sul pacchetto (`partner_imponibile`),
//  fissato alla proposta: se c'è, è quello.
//
//  I pacchetti proposti prima non ce l'hanno (NULL): per quelli lo si
//  ricalcola dal listino valet del partner, con la stessa formula del
//  preventivo (`numeriPacchetto`): prezzo annuo della fascia per
//  `valet_per_year` ÷ 12, per pickup all'anno, per mesi. La fascia si ritrova
//  da `included_km`, che alla proposta vale il `to_km` della fascia (o i km
//  arrotondati per eccesso sull'ultima fascia, aperta).
//
//  Limite del ricalcolo: è il prezzo con le tariffe di OGGI. Se
//  l'amministratore le ha cambiate dopo la proposta, il numero può differire
//  da quello proposto.
// -----------------------------------------------------------------------------

const arrotonda = (n) => Math.round(n * 100) / 100;

/** Le fasce valet DEL PARTNER (senza quelle generali Simba, che non hanno prezzi del partner). */
export async function listinoValetPartner(partnerId) {
  const res = await API.get('/valet-km-band', {
    params: { sort: 'from_km,ASC', limit: 100 },
    skipErrorToast: true,
  });
  return normalize(res).rows.filter((f) => f.partnerId && f.partnerId === partnerId);
}

/** Totale al cliente del pacchetto, senza IVA; `null` se non si può ricavare. */
export function prezzoClientePacchetto(m, fasce) {
  if (m?.partner_imponibile !== null && m?.partner_imponibile !== undefined) {
    return Number(m.partner_imponibile);
  }
  const pickup = Number(m?.valet_per_year);
  const mesi = Number(m?.duration_months) || (Number(m?.duration_years) || 0) * 12;
  const km = Number(m?.included_km);
  if (!fasce?.length || ![1, 2, 3].includes(pickup) || !mesi || !Number.isFinite(km) || m?.included_km == null) {
    return null;
  }
  const fascia = fasce.find((f) => f.to_km !== null && Number(f.to_km) === km)
    || fasce.find((f) => f.to_km === null && km >= Number(f.from_km));
  const annuo = fascia?.[`partner_annual_price_${pickup}`];
  if (annuo === null || annuo === undefined) return null;
  const mensilePerPickup = arrotonda(Number(annuo) / 12);
  return arrotonda(arrotonda(mensilePerPickup * pickup) * mesi);
}

export default {
  PAGE_SIZE,
  listinoValetPartner,
  prezzoClientePacchetto,
  clientiPagina,
  contaClienti,
  pacchettiPagina,
  contaPacchetti,
  pacchettiDeiClienti,
  consumiValet,
  tuttiIClienti,
  statoPacchetto,
  riepilogoValetCliente,
};
